import React, { useState, useEffect, Suspense } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  LogOut,
  AlertCircle,
  CheckCircle,
  Loader2,
  Edit2,
  Save,
  X,
  Building2,
  Calendar,
  Shield,
  Settings,
  Eye,
  EyeOff,
  Upload,
  Newspaper,
} from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";

useGLTF.preload("/models/2018__genesis_essentia_concept.glb");

function Model() {
  const { scene } = useGLTF("/models/2018__genesis_essentia_concept.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      style={{ width: "100%", height: "100%" }}
      className="w-full h-full"
    >
      <color attach="background" args={["#0f172a"]} />
      <Suspense fallback={null}>
        <Stage environment="city">
          <Model />
        </Stage>
        <Environment preset="city" />
        <OrbitControls autoRotate autoRotateSpeed={0.5} enableZoom={false} />
      </Suspense>
    </Canvas>
  );
}

// ✅ Options de spécialité selon le domaine (EN DEHORS DU COMPOSANT)
const specialiteOptions = {
  Automobile: ["mecanique", "carrosserie", "electricite"],
  Vente: ["Vente Supérieure", "Réception Après Vente", "GPME", "NDRC", "CCST"],
};

// ✅ Fonction pour formater l'affichage des spécialités
const formatSpecialite = (spec) => {
  const specialiteLabels = {
    'mecanique': '🔧 Mécanique',
    'carrosserie': '🚗 Carrosserie',
    'electricite': '⚡ Électricité',
    'Vente Supérieure': '💼 Vente Supérieure',
    'Réception Après Vente': '📋 Réception Après Vente',
    'GPME': '🏢 GPME (Gestion de la PME)',
    'NDRC': '💰 NDRC (Négociation et Digitalisation)',
    'CCST': '🎯 CCST (Conseiller Commercial)'
  };
  return specialiteLabels[spec] || spec;
};

const Profile = () => {
  const { user, logout, userRole } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    genre: "formateur",
    domain: "Automobile",
    specialite: "mecanique",
    canEditActualites: false,
    avatarUrl: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [userData, setUserData] = useState(null);

  // ✅ Récupère les données du profil au montage
  useEffect(() => {
    if (!user?.id) return;

    const fetchProfileData = async () => {
      try {
        console.log("📥 Récupère données profil pour:", user.id);

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select(
            "first_name, last_name, email, company_name, genre, domain, specialite, can_edit_actualites, created_at, avatar_url"
          )
          .eq("id", user.id)
          .single();

        if (profileError) {
          console.error("❌ Erreur fetch profiles:", profileError);
          return;
        }

        console.log("✅ Profil trouvé:", profileData);

        if (profileData) {
          setUserData({
            role: userRole,
            createdAt: profileData.created_at,
          });

          // Récupère l'URL de l'avatar
          let avatarUrl = "";
          if (profileData.avatar_url) {
            const { data } = supabase.storage
              .from("avatars")
              .getPublicUrl(profileData.avatar_url);
            avatarUrl = data.publicUrl;
            console.log("🖼️ Avatar URL:", avatarUrl);
          }

          // ✅ S'assurer que le domaine existe dans specialiteOptions
          const validDomain = specialiteOptions[profileData.domain] 
            ? profileData.domain 
            : "Automobile";
          
          // ✅ S'assurer que la spécialité existe pour ce domaine
          const validSpecialite = 
            profileData.specialite && specialiteOptions[validDomain].includes(profileData.specialite)
              ? profileData.specialite
              : specialiteOptions[validDomain][0];

          setFormData({
            firstName: profileData.first_name || "",
            lastName: profileData.last_name || "",
            email: profileData.email || user.email || "",
            companyName: profileData.company_name || "",
            genre: profileData.genre || "formateur",
            domain: validDomain,
            specialite: validSpecialite,
            canEditActualites: profileData.can_edit_actualites || false,
            avatarUrl: avatarUrl,
          });
        }
      } catch (err) {
        console.error("❌ Erreur chargement profil:", err);
      }
    };

    fetchProfileData();
  }, [user?.id, userRole]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "domain") {
      // Quand le domaine change, réinitialiser la spécialité
      const newDomain = value;
      const firstSpecialite = specialiteOptions[newDomain]?.[0] || "mecanique";
      setFormData((prev) => ({
        ...prev,
        domain: newDomain,
        specialite: firstSpecialite,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    setError("");
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    try {
      setLoading(true);
      setError("");

      const fileName = `${user.id}/${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: fileName })
        .eq("id", user.id);

      if (updateError) throw updateError;

      const { data } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      setFormData((prev) => ({
        ...prev,
        avatarUrl: data.publicUrl,
      }));

      setSuccess("✅ Avatar mis à jour avec succès");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      const errMsg = err.message || "Erreur lors de l'upload de l'avatar";
      console.error("❌ Erreur avatar:", errMsg);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (!user) throw new Error("Utilisateur non authentifié");

      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName,
          company_name: formData.companyName,
          genre: formData.genre,
          domain: formData.domain,
          specialite: formData.specialite,
          can_edit_actualites: formData.canEditActualites,
        })
        .eq("id", user.id);

      if (updateError) throw updateError;

      setSuccess("✅ Profil mis à jour avec succès");
      setIsEditing(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      const errMsg = err.message || "Erreur lors de la mise à jour du profil";
      console.error("❌ Erreur update:", errMsg);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError("Le mot de passe doit faire au moins 8 caractères");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (error) throw error;

      setSuccess("✅ Mot de passe modifié avec succès");
      setChangingPassword(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      const errMsg = err.message || "Erreur lors du changement de mot de passe";
      console.error("❌ Erreur password:", errMsg);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("❌ Erreur logout:", err);
      setError("Erreur lors de la déconnexion");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // ✅ Obtenir les spécialités disponibles pour le domaine actuel de manière sécurisée
  const availableSpecialites = specialiteOptions[formData.domain] || specialiteOptions.Automobile;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Canvas 3D en arrière-plan */}
      <div className="fixed inset-0 z-0">
        <CarScene />
      </div>

      {/* Loader */}
      <Loader />

      {/* Overlay semi-transparent */}
      <div className="fixed inset-0 bg-black/40 z-1" />

      {/* Contenu principal avec plus d'espace */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-40">
        {/* En-tête avec avatar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="relative inline-block"
          >
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center text-white text-5xl font-bold border-4 border-slate-700 shadow-2xl mx-auto mb-4 overflow-hidden">
              {formData.avatarUrl ? (
                <img
                  src={formData.avatarUrl}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{formData.firstName?.[0] || "?"}</span>
              )}
            </div>
            <label
              htmlFor="avatar-upload"
              className="absolute bottom-2 right-2 bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-full cursor-pointer transition-all shadow-lg"
            >
              <Upload className="w-4 h-4" />
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </label>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-4xl font-bold text-white mb-2"
          >
            {formData.firstName} {formData.lastName}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-slate-300 flex items-center justify-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {formData.email}
          </motion.p>

          {formData.canEditActualites && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-blue-600/30 border border-blue-500/50 rounded-full"
            >
              <Newspaper className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-200 font-medium">Éditeur d'actualités</span>
            </motion.div>
          )}
        </motion.div>

        {/* Messages d'erreur/succès */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-200">{error}</p>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-200">{success}</p>
          </motion.div>
        )}

        {/* Formulaire de profil */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleUpdateProfile}
          className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <User className="w-5 h-5 text-purple-400" />
              </motion.div>
              Informations personnelles
            </h3>
            {!isEditing ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                type="button"
                onClick={() => setIsEditing(true)}
                className="text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                Modifier
              </motion.button>
            ) : (
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  type="submit"
                  disabled={loading}
                  className="text-green-400 hover:text-green-300 text-sm font-medium flex items-center gap-2 disabled:opacity-70"
                >
                  <Save className="w-4 h-4" />
                  Enregistrer
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="text-red-400 hover:text-red-300 text-sm font-medium flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Annuler
                </motion.button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Prénom */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Prénom
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all text-sm"
                  required
                />
              ) : (
                <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                  {formData.firstName || "Non renseigné"}
                </p>
              )}
            </div>

            {/* Nom */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Nom
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all text-sm"
                  required
                />
              ) : (
                <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                  {formData.lastName || "Non renseigné"}
                </p>
              )}
            </div>

            {/* Email (non modifiable) */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Email
              </label>
              <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                {formData.email}
              </p>
            </div>

            {/* Entreprise */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Entreprise
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all text-sm"
                />
              ) : (
                <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                  {formData.companyName || "Non renseigné"}
                </p>
              )}
            </div>

            {/* Genre */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Genre
              </label>
              {isEditing ? (
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all text-sm"
                >
                  <option value="formateur">👨‍🏫 Formateur</option>
                  <option value="formatrice">👩‍🏫 Formatrice</option>
                  <option value="vie_scolaire">📚 Vie Scolaire</option>
                </select>
              ) : (
                <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                  {formData.genre === "formateur" ? "👨‍🏫 Formateur" : 
                   formData.genre === "formatrice" ? "👩‍🏫 Formatrice" : 
                   formData.genre === "vie_scolaire" ? "📚 Vie Scolaire" : formData.genre}
                </p>
              )}
            </div>

            {/* Domaine */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Domaine
              </label>
              {isEditing ? (
                <select
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all text-sm"
                >
                  <option value="Automobile">🚗 Automobile</option>
                  <option value="Vente">💼 Vente</option>
                </select>
              ) : (
                <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                  {formData.domain === "Automobile" ? "🚗 Automobile" : "💼 Vente"}
                </p>
              )}
            </div>

            {/* Spécialité */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Spécialité
              </label>
              {isEditing ? (
                <select
                  name="specialite"
                  value={formData.specialite}
                  onChange={handleInputChange}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all text-sm"
                >
                  {availableSpecialites.map((spec) => (
                    <option key={spec} value={spec}>
                      {formatSpecialite(spec)}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                  {formatSpecialite(formData.specialite) || "Non renseigné"}
                </p>
              )}
            </div>

            {/* Option éditeur */}
            <div>
              <label className="block text-xs font-medium mb-2 text-white">
                Éditeur d'actualités
              </label>
              {isEditing ? (
                <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <input
                    type="checkbox"
                    id="canEditActualites"
                    name="canEditActualites"
                    checked={formData.canEditActualites}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded border-slate-700 text-purple-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-0"
                  />
                  <label
                    htmlFor="canEditActualites"
                    className="text-sm text-white cursor-pointer"
                  >
                    Peut éditer les actualités
                  </label>
                </div>
              ) : (
                <p className="text-white bg-slate-800/30 rounded-lg p-3 text-sm">
                  {formData.canEditActualites ? "✅ Oui" : "❌ Non"}
                </p>
              )}
            </div>
          </div>
        </motion.form>

        {/* Informations supplémentaires en grille */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
        >
          {/* Entreprise */}
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Building2 className="w-5 h-5 text-purple-400" />
              </motion.div>
              Entreprise
            </h3>
            <div className="space-y-3 text-slate-300 text-sm">
              <div>
                <p className="text-slate-400 text-xs">Nom</p>
                <p className="text-white">
                  {formData.companyName || "Non renseigné"}
                </p>
              </div>
            </div>
          </div>

          {/* Compte */}
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Settings className="w-5 h-5 text-purple-400" />
              </motion.div>
              Compte
            </h3>
            <div className="space-y-3 text-slate-300 text-sm">
              <div>
                <p className="text-slate-400 text-xs">Rôle</p>
                <p className="text-white capitalize">{userRole}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Créé le</p>
                <p className="text-white flex items-center gap-2">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Calendar className="w-4 h-4" />
                  </motion.div>
                  {formatDate(userData?.createdAt)}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Changement de mot de passe */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Shield className="w-5 h-5 text-purple-400" />
              </motion.div>
              Sécurité
            </h3>
            {!changingPassword && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => setChangingPassword(true)}
                className="text-purple-400 hover:text-purple-300 text-sm font-medium"
              >
                Changer le mot de passe
              </motion.button>
            )}
          </div>

          {changingPassword && (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleChangePassword}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-medium mb-2 text-white">
                  Mot de passe actuel
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all pr-10 text-sm"
                    placeholder="••••••••"
                    required
                  />
                  <motion.button
                    type="button"
                    onClick={() =>
                      setShowCurrentPassword(!showCurrentPassword)
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    whileHover={{ scale: 1.1 }}
                  >
                    {showCurrentPassword ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </motion.button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-2 text-white">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all pr-10 text-sm"
                    placeholder="••••••••"
                    required
                  />
                  <motion.button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    whileHover={{ scale: 1.1 }}
                  >
                    {showNewPassword ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </motion.button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-2 text-white">
                  Confirmer le nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all pr-10 text-sm"
                    placeholder="••••••••"
                    required
                  />
                  <motion.button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    whileHover={{ scale: 1.1 }}
                  >
                    {showConfirmPassword ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </motion.button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 text-sm"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Changement...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Changer le mot de passe</span>
                    </>
                  )}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setChangingPassword(false)}
                  className="bg-slate-700 hover:bg-slate-600 text-white font-medium py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
                >
                  <X className="w-4 h-4" />
                  Annuler
                </motion.button>
              </div>
            </motion.form>
          )}

          {!changingPassword && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-blue-900/30 border border-blue-700/40 rounded-lg p-3"
            >
              <p className="text-xs text-blue-200">
                💡 Gardez votre mot de passe sécurisé et unique. Ne le partagez
                jamais avec quiconque.
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Bouton déconnexion */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
        >
          <LogOut className="w-5 h-5" />
          Se déconnecter
        </motion.button>
      </div>
    </div>
  );
};

export default Profile;